import React, { useState, useEffect, useContext } from "react";
import { TranslatorContext } from "../../context/Translator";
import { Link, useParams, useNavigate } from "react-router-dom";
import { Row, Col, Card, Badge, Button, Alert, Form, Modal } from "react-bootstrap";
import PageLayout from "../../layouts/PageLayout";
import gastosData from "../../assets/data/gastos.json";

export default function GastoValidacionPage() {
    const { t } = useContext(TranslatorContext);
    const { id } = useParams();
    const navigate = useNavigate();
    const [gasto, setGasto] = useState(null);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [accion, setAccion] = useState('');
    const [validacion, setValidacion] = useState({
        estado: '',
        observaciones: '',
        validador: 'Usuario Actual', // En producción vendría del contexto de auth
        fecha_validacion: new Date().toISOString().split('T')[0]
    });

    useEffect(() => {
        // Buscar el gasto por ID
        const gastoEncontrado = gastosData.tbody.find(item => item.id === id);
        setGasto(gastoEncontrado);
    }, [id]);

    if (!gasto) {
        return (
            <PageLayout>
                <Alert variant="warning">Gasto no encontrado</Alert>
            </PageLayout>
        );
    }

    if (gasto.status !== 'Pendiente') {
        return (
            <PageLayout>
                <Alert variant="info">
                    Este gasto ya ha sido validado. Estado actual: <Badge bg="secondary">{gasto.status}</Badge>
                </Alert>
                <Link to={`/gastos/ver/${id}`}>
                    <Button variant="primary">Ver Detalle del Gasto</Button>
                </Link>
            </PageLayout>
        );
    }

    const formatCurrency = (amount) => {
        const numAmount = typeof amount === 'string' ? 
            parseInt(amount.replace(/\./g, '')) : amount;
        
        return new Intl.NumberFormat('es-CL', {
            style: 'currency',
            currency: 'CLP',
            minimumFractionDigits: 0
        }).format(numAmount);
    };

    const getCategoryColor = (category) => {
        const colors = {
            'Gastos Oficina': '#dc3545',
            'Edmundo Neira': '#0dcaf0',
            'Operativa/Terreno': '#198754',
            'Egresos Operativos': '#ffc107'
        };
        return colors[category] || '#6c757d';
    };

    const handleInputChange = (e) => {
        setValidacion(prev => ({
            ...prev,
            [e.target.name]: e.target.value
        }));
    };

    const abrirConfirmacion = (nuevaAccion) => {
        setAccion(nuevaAccion);
        setValidacion(prev => ({
            ...prev,
            estado: nuevaAccion
        }));
        setShowConfirmModal(true);
    };

    const confirmarValidacion = () => {
        // Aquí iría la lógica para enviar la validación al backend
        console.log('Validación enviada:', {
            gastoId: id,
            ...validacion
        });

        // Simular actualización exitosa
        setShowConfirmModal(false);
        
        // Mostrar mensaje de éxito y redirigir
        alert(`Gasto ${accion.toLowerCase()} exitosamente`);
        navigate('/listado-gastos');
    };

    const criteriosValidacion = [
        { criterio: 'Documento adjunto presente', cumple: !!gasto.documento, requerido: true },
        { criterio: 'Monto dentro del presupuesto', cumple: parseInt(gasto.comuna.replace(/\./g, '')) < 500000, requerido: true },
        { criterio: 'Categoría correctamente asignada', cumple: true, requerido: true },
        { criterio: 'Responsable autorizado', cumple: true, requerido: true },
        { criterio: 'Fecha del gasto válida', cumple: new Date(gasto.brand) <= new Date(), requerido: true },
        { criterio: 'Descripción detallada', cumple: gasto.describe && gasto.describe.length > 10, requerido: false }
    ];

    const criteriosCumplidos = criteriosValidacion.filter(c => c.cumple).length;
    const criteriosRequeridos = criteriosValidacion.filter(c => c.requerido && c.cumple).length;
    const criteriosRequiredosTotal = criteriosValidacion.filter(c => c.requerido).length;

    const puedeAprobar = criteriosRequeridos === criteriosRequiredosTotal;

    return (
        <PageLayout>
            <Row>
                <Col xl={12}>
                    <div className="mc-card">
                        <div className='mc-breadcrumb'>
                            <h3 className="mc-breadcrumb-title">Validación de Gasto</h3>
                            <ul className="mc-breadcrumb-list">
                                <li className="mc-breadcrumb-item"><Link to='#' className="mc-breadcrumb-link">{t('home')}</Link></li>
                                <li className="mc-breadcrumb-item"><Link to='/listado-gastos' className="mc-breadcrumb-link">Gastos</Link></li>
                                <li className="mc-breadcrumb-item">Validar</li>
                            </ul>
                        </div>
                    </div>
                </Col>

                {/* Información del gasto */}
                <Col xl={8}>
                    <Card className="mc-card mb-4">
                        <Card.Header>
                            <h5>Información del Gasto a Validar</h5>
                        </Card.Header>
                        <Card.Body>
                            <div className="border-start border-4 ps-3 mb-4" 
                                 style={{ borderColor: `${getCategoryColor(gasto.category)} !important` }}>
                                <div className="d-flex justify-content-between align-items-start">
                                    <div>
                                        <h5 className="mb-1">{gasto.heading}</h5>
                                        <p className="text-muted mb-2">{gasto.describe}</p>
                                        <span className="badge bg-light text-dark">{gasto.id}</span>
                                    </div>
                                    <div className="text-end">
                                        <h4 className="mb-0" style={{ color: getCategoryColor(gasto.category) }}>
                                            {formatCurrency(gasto.comuna)}
                                        </h4>
                                        <small className="text-muted">{gasto.category}</small>
                                    </div>
                                </div>
                            </div>

                            <Row>
                                <Col md={6}>
                                    <div className="mb-3">
                                        <label className="text-muted small">Responsable</label>
                                        <div><strong>{gasto.responsable}</strong></div>
                                    </div>
                                </Col>
                                <Col md={6}>
                                    <div className="mb-3">
                                        <label className="text-muted small">Fecha del Gasto</label>
                                        <div><strong>{new Date(gasto.brand).toLocaleDateString('es-CL')}</strong></div>
                                    </div>
                                </Col>
                                <Col md={6}>
                                    <div className="mb-3">
                                        <label className="text-muted small">Fondo Asignado</label>
                                        <div><strong>{gasto.fondo}</strong></div>
                                    </div>
                                </Col>
                                <Col md={6}>
                                    <div className="mb-3">
                                        <label className="text-muted small">Tipo de Gasto</label>
                                        <div><strong>{gasto.tipo_gasto}</strong></div>
                                    </div>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    {/* Criterios de validación */}
                    <Card className="mc-card mb-4">
                        <Card.Header>
                            <h5>Criterios de Validación</h5>
                            <div>
                                <Badge bg="success">{criteriosCumplidos}/{criteriosValidacion.length} criterios cumplidos</Badge>
                                {puedeAprobar && <Badge bg="primary" className="ms-2">Listo para aprobar</Badge>}
                            </div>
                        </Card.Header>
                        <Card.Body>
                            <div className="list-group list-group-flush">
                                {criteriosValidacion.map((criterio, index) => (
                                    <div key={index} className="list-group-item d-flex justify-content-between align-items-center">
                                        <div className="d-flex align-items-center">
                                            <i className={`material-icons me-2 ${criterio.cumple ? 'text-success' : 'text-danger'}`}>
                                                {criterio.cumple ? 'check_circle' : 'cancel'}
                                            </i>
                                            <span>{criterio.criterio}</span>
                                            {criterio.requerido && <Badge bg="warning" className="ms-2">Requerido</Badge>}
                                        </div>
                                        <Badge bg={criterio.cumple ? 'success' : 'danger'}>
                                            {criterio.cumple ? 'Cumple' : 'No cumple'}
                                        </Badge>
                                    </div>
                                ))}
                            </div>
                        </Card.Body>
                    </Card>

                    {/* Formulario de validación */}
                    <Card className="mc-card">
                        <Card.Header>
                            <h5>Observaciones de Validación</h5>
                        </Card.Header>
                        <Card.Body>
                            <Form>
                                <Form.Group className="mb-3">
                                    <Form.Label>Observaciones</Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        rows={4}
                                        name="observaciones"
                                        value={validacion.observaciones}
                                        onChange={handleInputChange}
                                        placeholder="Ingrese observaciones sobre la validación..."
                                    />
                                </Form.Group>

                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Validador</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="validador"
                                                value={validacion.validador}
                                                onChange={handleInputChange}
                                                readOnly
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Fecha de Validación</Form.Label>
                                            <Form.Control
                                                type="date"
                                                name="fecha_validacion"
                                                value={validacion.fecha_validacion}
                                                onChange={handleInputChange}
                                                readOnly
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>
                            </Form>
                        </Card.Body>
                    </Card>
                </Col>

                {/* Panel de acciones */}
                <Col xl={4}>
                    <Card className="mc-card">
                        <Card.Header>
                            <h5>Acciones de Validación</h5>
                        </Card.Header>
                        <Card.Body>
                            <div className="d-grid gap-3">
                                <Button 
                                    variant="success" 
                                    size="lg"
                                    disabled={!puedeAprobar}
                                    onClick={() => abrirConfirmacion('Aprobado')}
                                >
                                    <i className="material-icons me-2">check_circle</i>
                                    Aprobar Gasto
                                </Button>

                                <Button 
                                    variant="danger" 
                                    size="lg"
                                    onClick={() => abrirConfirmacion('Rechazado')}
                                >
                                    <i className="material-icons me-2">cancel</i>
                                    Rechazar Gasto
                                </Button>

                                <Button 
                                    variant="warning" 
                                    size="lg"
                                    onClick={() => abrirConfirmacion('En Revisión')}
                                >
                                    <i className="material-icons me-2">schedule</i>
                                    Solicitar Correcciones
                                </Button>

                                <hr />

                                <Link to={`/gastos/ver/${id}`}>
                                    <Button variant="info" className="w-100">
                                        <i className="material-icons me-2">visibility</i>
                                        Ver Detalle Completo
                                    </Button>
                                </Link>

                                <Link to="/listado-gastos">
                                    <Button variant="secondary" className="w-100">
                                        <i className="material-icons me-2">arrow_back</i>
                                        Volver al Listado
                                    </Button>
                                </Link>
                            </div>

                            {!puedeAprobar && (
                                <Alert variant="warning" className="mt-3">
                                    <i className="material-icons me-2">warning</i>
                                    No se puede aprobar: faltan criterios requeridos
                                </Alert>
                            )}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Modal de confirmación */}
            <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Confirmar {accion}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="text-center mb-3">
                        <i className={`material-icons ${accion === 'Aprobado' ? 'text-success' : accion === 'Rechazado' ? 'text-danger' : 'text-warning'}`} 
                           style={{ fontSize: '48px' }}>
                            {accion === 'Aprobado' ? 'check_circle' : accion === 'Rechazado' ? 'cancel' : 'schedule'}
                        </i>
                    </div>
                    <p className="text-center">
                        ¿Está seguro que desea <strong>{accion.toLowerCase()}</strong> el gasto "{gasto.heading}"?
                    </p>
                    <div className="bg-light p-3 rounded">
                        <strong>Monto:</strong> {formatCurrency(gasto.comuna)}<br />
                        <strong>Responsable:</strong> {gasto.responsable}<br />
                        <strong>Validador:</strong> {validacion.validador}
                    </div>
                    {validacion.observaciones && (
                        <div className="mt-3">
                            <strong>Observaciones:</strong>
                            <div className="bg-light p-2 rounded mt-1">
                                {validacion.observaciones}
                            </div>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowConfirmModal(false)}>
                        Cancelar
                    </Button>
                    <Button 
                        variant={accion === 'Aprobado' ? 'success' : accion === 'Rechazado' ? 'danger' : 'warning'}
                        onClick={confirmarValidacion}
                    >
                        Confirmar {accion}
                    </Button>
                </Modal.Footer>
            </Modal>
        </PageLayout>
    );
}
